import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Table, Input, Button, Popconfirm } from "antd";
import { SearchOutlined, InboxOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import moment from "moment";

const IndexTable = (props) => {
  const {
    editableCell,
    cancel,
    isEditing,
    dataSource,
    editingKey,
    edit,
    save,
    deletefn,
  } = props;
  const onCellConfig = {
    body: {
      cell: editableCell,
    },
  };
  const paginationConfig = {
    onChange: cancel,
  };

  const [searchedColumn, setSearchedColumn] = useState();
  const [searchText, setSearchText] = useState();
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters, confirm)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : false,
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select());
      }
    },
    render: (text) =>
      searchedColumn === dataIndex && text ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text.toString()}
        />
      ) : (
        text
      ),
  });

  const columnSet = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "200px",
      editable: true,
      ...getColumnSearchProps("name"),
      sorter: (a, b) => (a.name ? a.name.localeCompare(b.name) : null),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "150px",
      editable: true,
      ...getColumnSearchProps("type"),
      sorter: (a, b) => (a.type ? a.type.localeCompare(b.type) : null),
    },
    {
      title: "Format",
      dataIndex: "format",
      key: "format",
      width: "200px",
      editable: true,
      ...getColumnSearchProps("format"),
      sorter: (a, b) => {
        return a.format
          ? a.format
              .toString()
              .localeCompare(b.format ? b.format.toString() : "")
          : "".localeCompare(b.format ? b.format.toString() : "");
      },
    },
    {
      title: "Wildcard",
      dataIndex: "wildcard",
      key: "wildcard",
      width: "150px",
      editable: true,
      ...getColumnSearchProps("wildcard"),
      sorter: (a, b) =>
        (![undefined, null].includes(a.wildcard) ? a.wildcard : 99999999) -
        (![undefined, null].includes(b.wildcard) ? b.wildcard : 99999999),
    },
    {
      title: "Order",
      dataIndex: "order",
      key: "order",
      width: "150px",
      editable: true,
      ...getColumnSearchProps("order"),
      sorter: (a, b) => {
        return (
          (typeof a.order === "number" ? a.order : 99999999) -
          (typeof b.order === "number" ? b.order : 99999999)
        );
      },
    },
    {
      title: "Required",
      dataIndex: "required",
      key: "required",
      width: "150px",
      render: (_, record) => (
        <span>
          {typeof record.required === "boolean"
            ? record.required.toString()
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (typeof a.required === "boolean"
          ? a.required.toString()
          : ""
        ).localeCompare(
          typeof b.required === "boolean" ? b.required.toString() : "",
        ),
      editable: true,
    },
    {
      title: "Default Index",
      dataIndex: "defaultIndex",
      key: "defaultIndex",
      width: "150px",
      render: (_, record) => (
        <span>
          {typeof record.defaultIndex === "boolean"
            ? record.defaultIndex.toString()
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (typeof a.defaultIndex === "boolean"
          ? a.defaultIndex.toString()
          : ""
        ).localeCompare(
          typeof b.defaultIndex === "boolean" ? b.defaultIndex.toString() : "",
        ),
      editable: true,
    },
    {
      title: "Editable",
      dataIndex: "editable",
      key: "editable",
      width: "150px",
      render: (_, record) => (
        <span>
          {typeof record.editable === "boolean"
            ? record.editable.toString()
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (typeof a.editable === "boolean"
          ? a.editable.toString()
          : ""
        ).localeCompare(
          typeof b.editable === "boolean" ? b.editable.toString() : "",
        ),
      editable: true,
    },
    {
      title: "Available In IS",
      dataIndex: "availableInIS",
      key: "availableInIS",
      width: "150px",
      render: (_, record) => (
        <span>
          {typeof record.availableInIS === "boolean"
            ? record.availableInIS.toString()
            : ""}
        </span>
      ),
      sorter: (a, b) =>
        (typeof a.availableInIS === "boolean"
          ? a.availableInIS.toString()
          : ""
        ).localeCompare(
          typeof b.availableInIS === "boolean"
            ? b.availableInIS.toString()
            : "",
        ),
      editable: true,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: "150px",
      render: (_, record) => (
        <span>{Array.isArray(record.tags) ? record.tags.join(", ") : ""}</span>
      ),
      // sorter:(a,b)=>(typeof a.tags==='boolean'?a.tags.toString():'').localeCompare(typeof b.tags==='boolean'?b.tags.toString():''),
      editable: true,
    },
    {
      title: "Created by",
      dataIndex: "create_by",
      key: "create_by",
      width: "200px",
      render: (_, record) => {
        return (
          <span>
            {record.create_by !== undefined && record.create_by.name
              ? record.create_by.name
              : ""}
          </span>
        );
      },
      editable: false,
    },
    {
      title: "Created at",
      dataIndex: "create_at",
      key: "create_at",
      width: "180px",
      editable: false,
      render: (_, record) => (
        <span>
          {record.create_at
            ? moment(record.create_at).local().format("YYYY-MM-DD HH:mm:ss")
            : ""}
        </span>
      ),
    },
    {
      title: "Updated by",
      dataIndex: "update_by",
      key: "update_by",
      width: "200px",
      render: (_, record) => (
        <span>
          {record.update_by !== undefined && record.update_by.name
            ? record.update_by.name
            : ""}
        </span>
      ),
      editable: false,
    },
    {
      title: "Updated at",
      dataIndex: "update_at",
      key: "update_at",
      width: "180px",
      editable: false,
      render: (_, record) => (
        <span>
          {record.update_at
            ? moment(record.update_at).local().format("YYYY-MM-DD HH:mm:ss")
            : ""}
        </span>
      ),
    },
    {
      title: "Operation",
      dataIndex: "operation",
      fixed: "right",
      width: "180px",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <a
              onClick={() => save(record._id)}
              style={{
                marginRight: 8,
              }}
            >
              Save
            </a>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <a
                style={{
                  marginRight: 8,
                }}
              >
                Cancel
              </a>
            </Popconfirm>
            <Popconfirm title="Sure to delete?" onConfirm={deletefn}>
              <a>Delete</a>
            </Popconfirm>
          </span>
        ) : (
          <a disabled={editingKey !== null} onClick={() => edit(record)}>
            Edit
          </a>
        );
      },
    },
  ].map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  return (
    <Table
      className="sm-custom-column-header"
      components={onCellConfig}
      bordered
      dataSource={dataSource}
      columns={columnSet}
      rowClassName="editable-row"
      pagination={paginationConfig}
      scroll={{ x: 2390 }}
      rowKey="_id"
    />
  );
};

IndexTable.propTypes = {};

export default IndexTable;
